import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Paper,
} from '@mui/material';

import { LogTable, LogStatsCard } from 'components';
import {
  useGetStakeLogs,
  useGetWithdrawLogs,
  useGetRewardLogs,
  useGetUserLogs,
  useGetRecentLogs,
  useGetLogStats,
} from 'hooks/useLogApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: { xs: 1, md: 3 } }}>{children}</Box>}
  </div>
);

const LogsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [userAddress, setUserAddress] = useState('');
  const [referrerAddress, setReferrerAddress] = useState('');
  const [limit, setLimit] = useState(20);

  // 用于实际查询的状态（只有点击查询按钮时才更新）
  const [queryUserAddress, setQueryUserAddress] = useState('');
  const [queryReferrerAddress, setQueryReferrerAddress] = useState('');
  const [queryLimit, setQueryLimit] = useState(20);

  // API hooks - 使用查询状态而不是输入状态
  const { data: stakeLogs, isLoading: stakeLoading } = useGetStakeLogs({
    user: queryUserAddress || undefined,
    limit: queryLimit,
  });

  const { data: withdrawLogs, isLoading: withdrawLoading } = useGetWithdrawLogs({
    user: queryUserAddress || undefined,
    limit: queryLimit,
  });

  const { data: rewardLogs, isLoading: rewardLoading } = useGetRewardLogs({
    user: queryUserAddress || undefined,
    limit: queryLimit,
  });

  const { data: userLogs, isLoading: userLoading } = useGetUserLogs(
    queryUserAddress ? { address: queryUserAddress, limit: queryLimit } : { address: '', limit: queryLimit },
  );

  const { data: recentLogs, isLoading: recentLoading } = useGetRecentLogs({ limit: queryLimit });

  const { data: logStats, isLoading: statsLoading } = useGetLogStats();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // 处理查询按钮点击
  const handleQuery = () => {
    setQueryUserAddress(userAddress);
    setQueryReferrerAddress(referrerAddress);
    setQueryLimit(limit);
  };

  // 处理重置按钮点击
  const handleReset = () => {
    setUserAddress('');
    setReferrerAddress('');
    setLimit(20);
    setQueryUserAddress('');
    setQueryReferrerAddress('');
    setQueryLimit(20);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 0 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          区块链日志查询
        </Typography>

        {/* 统计卡片 */}
        <Box sx={{ mb: { xs: 2, md: 4 } }}>
          {logStats && <LogStatsCard stats={logStats.stats} loading={statsLoading} />}
        </Box>

        {/* 过滤器 */}
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            查询过滤器
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="用户地址"
                value={userAddress}
                onChange={(e) => setUserAddress(e.target.value)}
                placeholder="0x..."
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="推荐人地址"
                value={referrerAddress}
                onChange={(e) => setReferrerAddress(e.target.value)}
                placeholder="0x..."
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="number"
                label="查询数量"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                inputProps={{ min: 1, max: 1000 }}
                size="small"
              />
            </Grid>
            <Grid item xs={3} sm={1.5} md={1}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleQuery}
                sx={{ height: { xs: '40px', md: '40px' } }}
              >
                查询
              </Button>
            </Grid>
            <Grid item xs={3} sm={1.5} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                sx={{ height: { xs: '40px', md: '40px' } }}
              >
                重置
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* 标签页 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="质押日志" />
            <Tab label="提现日志" />
            <Tab label="奖励日志" />
            <Tab label="用户日志" />
            <Tab label="最近活动" />
          </Tabs>
        </Box>

        {/* 质押日志 */}
        <TabPanel value={tabValue} index={0}>
          <LogTable logs={stakeLogs?.logs || []} loading={stakeLoading} />
        </TabPanel>

        {/* 提现日志 */}
        <TabPanel value={tabValue} index={1}>
          <LogTable logs={withdrawLogs?.logs || []} loading={withdrawLoading} />
        </TabPanel>

        {/* 奖励日志 */}
        <TabPanel value={tabValue} index={2}>
          <LogTable logs={rewardLogs?.logs || []} loading={rewardLoading} />
        </TabPanel>

        {/* 用户所有日志 */}
        <TabPanel value={tabValue} index={3}>
          <LogTable logs={userLogs?.logs || []} loading={userLoading} />
        </TabPanel>

        {/* 最近活动 */}
        <TabPanel value={tabValue} index={4}>
          <LogTable logs={recentLogs?.logs || []} loading={recentLoading} />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default LogsPage;
